import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface ApiScope {
  value: string;
  description: string;
}

export type ApiKeyStatus = 'ACTIVE' | 'ROTATING' | 'REVOKED' | 'EXPIRED';

/** The client a key or webhook belongs to, so a cross-tenant list can name its owner. */
export interface OwningTenant {
  id: number;
  tenant_name: string | null;
  tenant_subdomain: string | null;
}

export interface ApiKeyRecord {
  id: number;
  key_prefix: string;
  /** live | test | webhook — the list query has always returned it; nothing displayed it. */
  environment: string;
  label: string | null;
  agent_id: number | null;
  tenant_id: number;
  is_active: boolean;
  status: ApiKeyStatus;
  rotating_until: string | null;
  rotated_to_id: number | null;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  agent: { id: number; name: string; agent_code: string | null } | null;
  tenant: OwningTenant | null;
}

/** Why a key is on the attention list, and how long is left to act. */
export type ApiKeyAttentionReason = 'EXPIRING' | 'ROTATING' | 'ROTATION_OVERDUE';

export interface ApiKeyNeedingAttention {
  id: number;
  key_prefix: string;
  label: string | null;
  status: ApiKeyStatus;
  environment: string;
  expires_at: string | null;
  rotating_until: string | null;
  tenant_id: number;
  tenant: OwningTenant | null;
  reason: ApiKeyAttentionReason;
  days_left: number | null;
}

export interface GeneratedApiKey extends ApiKeyRecord {
  key: string; // plain key — shown once
}

export interface RotatedApiKey {
  id: number;
  key: string; // plain key — shown once
  prefix: string;
  rotated_from_id: number;
  old_key_valid_until: string;
}

export interface WebhookEventOption {
  event: string;
  /** False for an event the platform does not raise yet — subscribing to it receives nothing. */
  emitted: boolean;
}

export interface WebhookRecord {
  id: number;
  url: string;
  label: string | null;
  events: string[];
  agent_id: number | null;
  tenant_id: number;
  is_active: boolean;
  last_triggered_at: string | null;
  failure_count: number;
  created_at: string;
  agent: { id: number; name: string; agent_code: string | null } | null;
  tenant: OwningTenant | null;
}

/** A change made to a webhook, shown in its history beside the attempts. */
export interface WebhookAuditEntry {
  kind: 'AUDIT';
  id: number;
  action: string;
  actor_id: number | null;
  created_at: string;
}

/** One HTTP attempt at delivering an event. `status_code` is null when nothing answered. */
export interface WebhookDelivery {
  kind: 'DELIVERY';
  id: number;
  webhook_id: number;
  tenant_id: number;
  event: string;
  delivery_id: string;
  attempt: number;
  success: boolean;
  status_code: number | null;
  error: string | null;
  duration_ms: number | null;
  /** True for a probe sent from this portal rather than a real event. */
  is_test: boolean;
  created_at: string;
}

/** A webhook's history is attempts and changes in one order. */
export type WebhookHistoryEntry = WebhookDelivery | WebhookAuditEntry;

/** What the client's endpoint answered to a deliberate test. */
export interface WebhookTestResult {
  success: boolean;
  status_code: number | null;
  error: string | null;
  duration_ms: number;
  delivery_id: string;
  event: string;
}

export interface WebhookDeadLetter {
  job_id: string;
  webhook_id: number;
  tenant_id: number;
  agent_id: number | null;
  event: string;
  url: string;
  delivery_id: string;
  attempts_made: number;
  failed_reason: string | null;
  failed_at: number | null;
  created_at: number | null;
}

export interface RegisteredWebhook extends WebhookRecord {
  secret: string; // plain secret — shown once
}

@Injectable({ providedIn: 'root' })
export class ApiKeyApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl;

  // ── API Keys ──────────────────────────────────────────────────────────────

  listScopes() {
    return this.http.get<{ data: ApiScope[] }>(`${this.base}/api-keys/scopes`);
  }

  generateKey(body: {
    agent_id?: number;
    tenant_id: number;
    label?: string;
    expires_at?: string;
    environment?: 'live' | 'test' | 'webhook';
    scopes?: string[];
    ip_whitelist?: string[];
    rate_limit_per_min?: number;
  }) {
    return this.http.post<{ data: GeneratedApiKey; status: boolean; message: string }>(
      `${this.base}/api-keys`,
      body,
    );
  }

  /** Repair-sync every key of one client into that client's own database. */
  syncKeysToTenant(tenantId: number) {
    return this.http.post<{ data: { synced: number }; status: boolean; message: string }>(
      `${this.base}/api-keys/sync-tenant/${tenantId}`,
      {},
    );
  }

  listKeysNeedingAttention(withinDays = 30) {
    return this.http.get<{ data: { result: ApiKeyNeedingAttention[]; total: number } }>(
      `${this.base}/api-keys/attention?within_days=${withinDays}`,
    );
  }

  listKeys(params: {
    agent_id?: number;
    tenant_id?: number;
    status?: string;
    environment?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }) {
    const q = new URLSearchParams();
    if (params.agent_id) q.set('agent_id', String(params.agent_id));
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.status) q.set('status', params.status);
    if (params.environment) q.set('environment', params.environment);
    if (params.q?.trim()) q.set('q', params.q.trim());
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    return this.http.get<{ data: { result: ApiKeyRecord[]; total: number } }>(
      `${this.base}/api-keys?${q}`,
    );
  }

  rotateKey(id: number, body?: { grace_hours?: number }) {
    return this.http.post<{ data: RotatedApiKey; status: boolean; message: string }>(
      `${this.base}/api-keys/${id}/rotate`,
      body ?? {},
    );
  }

  revokeKey(id: number) {
    return this.http.delete<{ status: boolean; message: string }>(`${this.base}/api-keys/${id}`);
  }

  // ── Webhooks ──────────────────────────────────────────────────────────────

  /** Mints a new signing secret for an existing webhook. Returned once; the row is untouched. */
  rotateWebhookSecret(id: number) {
    return this.http.post<{ data: { id: number; secret: string }; status: boolean; message: string }>(
      `${this.base}/webhooks/${id}/rotate`,
      {},
    );
  }

  testWebhook(id: number, event: string) {
    return this.http.post<{ data: WebhookTestResult; status: boolean; message: string }>(
      `${this.base}/webhooks/${id}/test`,
      { event },
    );
  }

  listWebhookDeliveries(
    id: number,
    params: {
      page?: number;
      page_size?: number;
      outcome?: 'accepted' | 'failed' | '';
      event?: string;
      is_test?: boolean;
      from?: string;
      to?: string;
    } = {},
  ) {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    if (params.outcome) q.set('outcome', params.outcome);
    if (params.event) q.set('event', params.event);
    // Explicitly compared: is_test=false is a filter for real events, not an absent one.
    if (params.is_test !== undefined) q.set('is_test', String(params.is_test));
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return this.http.get<{
      data: {
        result: WebhookHistoryEntry[];
        total: number;
        page: number;
        page_size: number;
        retention_days: number;
      };
    }>(`${this.base}/webhooks/${id}/deliveries?${q}`);
  }

  /** Edits a registered webhook in place. The signing secret is untouched and never returned. */
  updateWebhook(
    id: number,
    body: {
      url?: string;
      agent_id?: number | null;
      label?: string;
      events?: string[];
      is_active?: boolean;
    },
  ) {
    return this.http.patch<{ data: WebhookRecord; status: boolean; message: string }>(
      `${this.base}/webhooks/${id}`,
      body,
    );
  }

  registerWebhook(body: {
    url: string;
    tenant_id: number;
    agent_id?: number;
    label?: string;
    events?: string[];
  }) {
    return this.http.post<{ data: RegisteredWebhook; status: boolean; message: string }>(
      `${this.base}/webhooks`,
      body,
    );
  }

  listWebhooks(params: {
    tenant_id?: number;
    agent_id?: number;
    is_active?: boolean;
    event?: string;
    page?: number;
    page_size?: number;
  }) {
    const q = new URLSearchParams();
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.agent_id) q.set('agent_id', String(params.agent_id));
    // Explicitly compared to undefined: is_active=false is a filter, not an absent one.
    if (params.is_active !== undefined) q.set('is_active', String(params.is_active));
    if (params.event) q.set('event', params.event);
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    return this.http.get<{ data: { result: WebhookRecord[]; total: number } }>(
      `${this.base}/webhooks?${q}`,
    );
  }

  deleteWebhook(id: number) {
    return this.http.delete<{ status: boolean; message: string }>(`${this.base}/webhooks/${id}`);
  }

  listWebhookEvents() {
    return this.http.get<{ data: WebhookEventOption[] }>(`${this.base}/webhooks/events`);
  }

  listWebhookDeadLetters(params: { tenant_id?: number; page?: number; page_size?: number } = {}) {
    const q = new URLSearchParams();
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    return this.http.get<{
      data: {
        result: WebhookDeadLetter[];
        total: number;
        page: number;
        page_size: number;
        /** True when the failed set was too large to scan in full for this client. */
        truncated: boolean;
      };
    }>(`${this.base}/webhooks/dead-letters?${q}`);
  }

  replayWebhookDeadLetter(jobId: string) {
    return this.http.post<{ status: boolean; message: string; data: { job_id: string; status: string } }>(
      `${this.base}/webhooks/dead-letters/${jobId}/replay`,
      {},
    );
  }

  discardWebhookDeadLetter(jobId: string) {
    return this.http.delete<{ status: boolean; message: string }>(`${this.base}/webhooks/dead-letters/${jobId}`);
  }
}
